"use client";

import { useMemo } from "react";

import PlatformCard from "@/app/[locale]/(platform)/sharedComponents/platformCard";
import PlatformListItem from "@/app/[locale]/(platform)/sharedComponents/platformListItem";
import LomdaCard from "../../../lomda/lomdaCard";
import LomdaListItem from "../../../lomda/lomdaListItem";
import LomdaV2Card from "../../../lomdaV2/lomdaV2Card";
import LomdaV2ListItem from "../../../lomdaV2/lomdaV2ListItem";
import LomdaV3Card from "../../../lomdaV3/lomdaV3Card";
import LomdaV3ListItem from "../../../lomdaV3/lomdaV3ListItem";

import { useStorageQuery } from "../../../../hooks/storageQueryProvider";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useStorage } from "@/features/storage/useStorage";

import { formatIsoStringDate, formatSecondsToMinutes } from "@/lib/utils";
import { GetFilesResponseType } from "../../../../hooks/useGetFiles";
import { View } from "../../types";
import FileCheckbox from "./checkbox";
import { t } from "@/lib/localization";

type Props = {
    file: GetFilesResponseType["data"][0];
    view: View;
};

export default function FileCard({ file, view }: Props) {
    const { user } = useAuth();
    const { selectedFiles, settings, setSelectedFiles } = useStorageQuery();

    const [
        setVideoSheetOpen,
        setPresentationSheetOpen,
        setLomdaSheetOpen,
        setLomdaV2SheetOpen,
        setLomdaV3SheetOpen,
    ] = useStorage((state) => [
        state.setVideoSheetOpen,
        state.setPresentationSheetOpen,
        state.setLomdaSheetOpen,
        state.setLomdaV2SheetOpen,
        state.setLomdaV3SheetOpen,
    ]);

    const onFileClick = () => {
        if (!settings.files || !user?.id || !user?.libraryId) return;

        const shouldNavigate =
            settings.files.onFileClickActions.includes("navigate-to-file");

        const shouldSelect =
            settings.files.onFileClickActions.includes("select-file");

        if (shouldNavigate) {
            if (file.type === "video") {
                setVideoSheetOpen(true, {
                    libraryId: user.libraryId,
                    uid: user.id,
                    videoId: file.id,
                });
            }

            if (file.type === "presentation") {
                setPresentationSheetOpen(true, {
                    libraryId: user.libraryId,
                    presentationId: file.id,
                });
            }

            if (file.type === "lomda") {
                setLomdaSheetOpen(true, {
                    libraryId: user.libraryId,
                    lomdaId: file.id,
                });
            }

            if (file.type === "lomdaV2") {
                setLomdaV2SheetOpen(true, {
                    libraryId: user.libraryId,
                    lomdaId: file.id,
                });
            }

            if (file.type === "lomdaV3") {
                setLomdaV3SheetOpen(true, {
                    libraryId: user.libraryId,
                    lomdaId: file.id,
                });
            }
        }

        if (shouldSelect) {
            setSelectedFiles((prev) => {
                const isSelected = prev.some((f) => f.id === file.id);

                if (isSelected) {
                    if (shouldNavigate) return prev;
                    return prev.filter((f) => f.id !== file.id);
                }

                if (settings.selectedItemsMenu.mode === "single-file") {
                    return [file];
                }

                return [...prev, file];
            });
        }
    };

    const selected = useMemo(
        () => selectedFiles.some((f) => f.id === file.id),
        [selectedFiles, file.id]
    );

    const checkboxOption = settings.files?.menuOptions?.select
        ? {
              component: <FileCheckbox file={file} selected={selected} />,
              positionClassName: "top-3 end-3",
          }
        : undefined;

    const badgeText =
        file.type === "video"
            ? formatSecondsToMinutes(file.detailForBadge)
            : file.detailForBadge <= 1
              ? t("storage.presentation-card.num-of-slides-badge.single")
              : file.detailForBadge +
                " " +
                t("storage.presentation-card.num-of-slides-badge.multiple");

    const title = file.name || t("storage.no-name", undefined, undefined);

    const subtitle = `${
        file.type === "video"
            ? t("storage.video", undefined, undefined)
            : file.type === "lomda"
              ? t("storage.lomda", undefined, undefined)
              : file.type === "lomdaV2"
                ? t("storage.lomda-v2", undefined, undefined)
                : file.type === "lomdaV3"
                  ? t("storage.lomda-v3", undefined, undefined)
                  : t("storage.presentation", undefined, undefined)
    } · ${formatIsoStringDate(file.date)}`;

    // ---------- GRID VIEW ----------

    if (view === "grid") {
        if (file.type === "lomda") {
            return (
                <LomdaCard
                    onClick={onFileClick}
                    selected={selected}
                    file={file}
                    options={checkboxOption}
                />
            );
        }

        if (file.type === "lomdaV2") {
            return (
                <LomdaV2Card
                    onClick={onFileClick}
                    selected={selected}
                    file={file}
                    options={checkboxOption}
                />
            );
        }

        if (file.type === "lomdaV3") {
            return (
                <LomdaV3Card
                    onClick={onFileClick}
                    selected={selected}
                    file={file}
                    options={checkboxOption}
                />
            );
        }

        return (
            <PlatformCard
                onClick={onFileClick}
                selected={selected}
                thumbnail={{
                    src: file.thumbnailUrl,
                }}
                thumbnailBadge={{
                    text: badgeText,
                }}
                text={{
                    title,
                    subtitle,
                }}
                options={checkboxOption}
            />
        );
    }

    // ---------- LIST VIEW ----------

    if (file.type === "lomda") {
        return (
            <LomdaListItem
                onClick={onFileClick}
                selected={selected}
                file={file}
                options={
                    settings.files?.menuOptions?.select ? (
                        <FileCheckbox file={file} selected={selected} />
                    ) : undefined
                }
            />
        );
    }

    if (file.type === "lomdaV2") {
        return (
            <LomdaV2ListItem
                onClick={onFileClick}
                selected={selected}
                file={file}
                options={
                    settings.files?.menuOptions?.select ? (
                        <FileCheckbox file={file} selected={selected} />
                    ) : undefined
                }
            />
        );
    }

    if (file.type === "lomdaV3") {
        return (
            <LomdaV3ListItem
                onClick={onFileClick}
                selected={selected}
                file={file}
                options={
                    settings.files?.menuOptions?.select ? (
                        <FileCheckbox file={file} selected={selected} />
                    ) : undefined
                }
            />
        );
    }

    return (
        <PlatformListItem
            onClick={onFileClick}
            selected={selected}
            thumbnail={{
                src: file.thumbnailUrl,
            }}
            thumbnailBadge={{
                text: badgeText,
            }}
            text={{
                title,
                subtitle,
            }}
            options={
                settings.files?.menuOptions?.select ? (
                    <FileCheckbox file={file} selected={selected} />
                ) : undefined
            }
        />
    );
}
